import type { TFunction } from 'i18next';
import { isReservedSalesPointSlug } from 'pi-kiosk-shared';
import { tenantRelativePath, resolveBottomNavActiveTab, type BottomNavTab } from './bottomNavActiveTab.js';

/** Platform Nakupovat discovery routes — `/browse` and nested browse paths. */
export function isPlatformBrowsePath(pathname: string): boolean {
  return pathname === '/browse' || pathname.startsWith('/browse/');
}

/**
 * Canonical Home root only (`/browse` ± trailing slash) — HUX-01 compact chrome.
 * Nested `/browse/…` keeps journey depth Back / standard end cluster.
 */
export function isRootBrowseHomePath(pathname: string): boolean {
  if (pathname === '/browse' || pathname === '/browse/') {
    return true;
  }
  // Query/hash-only variants still count as root Home.
  const pathOnly = pathname.split(/[?#]/, 1)[0] ?? pathname;
  return pathOnly === '/browse' || pathOnly === '/browse/';
}

const TAB_TITLE_KEYS: Partial<Record<BottomNavTab, string>> = {
  browse: 'nav.browse',
  shop: 'nav.shop',
  scan: 'nav.scan',
  orders: 'nav.orders',
  shops: 'nav.shops',
  orgs: 'nav.orgs',
  more: 'nav.more',
  account: 'nav.account',
  receipts: 'nav.receipts',
  promo: 'nav.promo',
  overview: 'nav.account',
  donate: 'nav.donate',
  recurring: 'nav.donate',
  'my-donations': 'account.donations.title',
};

function parseSearchParams(search: string): URLSearchParams {
  if (search.length === 0) {
    return new URLSearchParams();
  }
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
}

function isPositiveSalesPointQueryId(raw: string | null): boolean {
  if (raw === null || !/^\d+$/.test(raw)) {
    return false;
  }
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0;
}

/**
 * True for concrete sales-point catalog chrome (`/{tenant}/shop?salesPointId=` /
 * legacy `?kioskId=` or `/{tenant}/{slug}`). Oblíbené hubs (`/favorites`,
 * `/account/shop`) stay false. Scanner depth (`/shop/scanner`) stays false —
 * Stage 3 keeps a simple scan header.
 */
export function isSalesPointCatalogChromePath(
  pathname: string,
  tenantCode: string,
  search: string = '',
): boolean {
  const rel = tenantRelativePath(pathname, tenantCode);
  const normalized =
    rel.length > 1 && rel.endsWith('/') ? rel.slice(0, -1) : rel;

  if (normalized === '/shop/scanner' || normalized.startsWith('/shop/scanner/')) {
    return false;
  }

  if (normalized === '/shop') {
    const params = parseSearchParams(search);
    // Prefer salesPointId; accept legacy kioskId for one-release dual-read.
    if (
      isPositiveSalesPointQueryId(params.get('salesPointId')) ||
      isPositiveSalesPointQueryId(params.get('kioskId'))
    ) {
      return true;
    }
    const slug = params.get('kioskSlug')?.trim() ?? '';
    return slug.length > 0;
  }

  // Single segment after tenant → kiosk slug (not a reserved retail hub).
  if (normalized.startsWith('/') && !normalized.slice(1).includes('/')) {
    const seg = normalized.slice(1);
    if (seg.length === 0) {
      return false;
    }
    return !isReservedSalesPointSlug(seg);
  }

  return false;
}

export interface ResolveShellHeaderTitleOptions {
  /** `location.search` — required to detect `/{tenant}/shop?salesPointId=` / legacy `?kioskId=`. */
  readonly search?: string;
  /**
   * Place / kiosk display name when on a sales-point catalog route.
   * When set, wins over tab labels (Stage 3 §5 / A6 shop chrome).
   */
  readonly placeTitle?: string | null;
}

/** Route-aware shell header title — place on SP catalog; else active tab label. */
export function resolveShellHeaderTitle(
  pathname: string,
  tenantCode: string,
  t: TFunction<'customer'>,
  options?: ResolveShellHeaderTitleOptions,
): string {
  const place = options?.placeTitle?.trim() ?? '';
  if (place.length > 0) {
    return place;
  }
  const search = options?.search ?? '';
  // Stage 2 browse discovery shell — center title hidden; page chrome owns heading.
  if (isPlatformBrowsePath(pathname)) {
    return '';
  }
  // Sales-point path before meta resolves — prefer Obchod over Oblíbené tab label.
  if (isSalesPointCatalogChromePath(pathname, tenantCode, search)) {
    return t('shop.title');
  }
  const tab = resolveBottomNavActiveTab(pathname, tenantCode);
  const key = TAB_TITLE_KEYS[tab] ?? 'nav.browse';
  return t(key);
}
